// 四語系（zh-Hant 權威），同 home-base（app/home-base/web/src/i18n/README.md）。
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import ja from './ja.json';
import zhHans from './zh-Hans.json';
import zhHant from './zh-Hant.json';
// dev 命名空間：只在 dev plane 出現的開發者畫面（原工位頁；工位頁 10-22 搬到 develop portal，字串暫留給之後的 dev 畫面）。
// 用法 t('dev:seat.title') / useTranslation('dev')；跟產品字串分開，產品翻譯不用管這份。
import devEn from './dev/en.json';
import devJa from './dev/ja.json';
import devZhHans from './dev/zh-Hans.json';
import devZhHant from './dev/zh-Hant.json';
// kmgr 命名空間：Kinio KMS 操作台（features/kmgr，SPEC #kinio-kms-ui）。用法 useTranslation('kmgr') / t('kmgr:nav.incidents')。
import kmgrEn from './kmgr/en.json';
import kmgrJa from './kmgr/ja.json';
import kmgrZhHans from './kmgr/zh-Hans.json';
import kmgrZhHant from './kmgr/zh-Hant.json';
// siteroom 命名空間：機房（features/siteroom，plan SPEC #kinio-station-seat-site-share）。用法 useTranslation('siteroom')。
import siteroomEn from './siteroom/en.json';
import siteroomJa from './siteroom/ja.json';
import siteroomZhHans from './siteroom/zh-Hans.json';
import siteroomZhHant from './siteroom/zh-Hant.json';
// energy 命名空間：能源管理（features/energy，plan SPEC #energy-ui-vms）。用法 t('energy:title')；{leaf} 寫 $t(translation:leaf.cur)。
import energyEn from './energy/en.json';
import energyJa from './energy/ja.json';
import energyZhHans from './energy/zh-Hans.json';
import energyZhHant from './energy/zh-Hant.json';
import kinioUiStrings from '../lib/kinio_ui_strings.json';
import { leafVars } from '../lib/leafTerm';

export const LANGS = [
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'zh-Hans', label: '简体中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
] as const;

const STORAGE_KEY = 'kinio.vms.lang';

function initialLang(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.some((l) => l.code === saved)) return saved;
  } catch {
    /* 無 storage */
  }
  const nav = (globalThis.navigator?.language ?? 'zh-Hant').toLowerCase();
  if (nav.startsWith('zh-cn') || nav.startsWith('zh-sg') || nav === 'zh-hans') return 'zh-Hans';
  if (nav.startsWith('zh')) return 'zh-Hant';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('en')) return 'en';
  return 'zh-Hant';
}

/** kinio_ui_strings.json 的一個語系 → kinio_model namespace（平鋪，key 含點號）。 */
function kinioModelNs(lang: 'en' | 'ja' | 'zh-Hans'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, row] of Object.entries(kinioUiStrings.strings as Record<string, Record<string, string>>)) {
    if (row[lang]) out[id] = row[lang];
  }
  return out;
}

// <html lang> 跟著目前語系（含初次載入）：日文畫面漢字才用日文字形，不吃 index.html 的 zh-Hant
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

void i18n.use(initReactI18next).init({
  // kinio_model：能力投影文案的翻譯表（key = label_id，lib/kinioLabel.ts）；zh-Hant 是投影原文，不建表
  resources: {
    'zh-Hant': { translation: zhHant, kinio_model: {}, dev: devZhHant, kmgr: kmgrZhHant, siteroom: siteroomZhHant, energy: energyZhHant },
    'zh-Hans': { translation: zhHans, kinio_model: kinioModelNs('zh-Hans'), dev: devZhHans, kmgr: kmgrZhHans, siteroom: siteroomZhHans, energy: energyZhHans },
    en: { translation: en, kinio_model: kinioModelNs('en'), dev: devEn, kmgr: kmgrEn, siteroom: siteroomEn, energy: energyEn },
    ja: { translation: ja, kinio_model: kinioModelNs('ja'), dev: devJa, kmgr: kmgrJa, siteroom: siteroomJa, energy: energyJa },
  },
  lng: initialLang(),
  fallbackLng: 'zh-Hant',
  interpolation: { escapeValue: false },
});

export function setLanguage(code: string): void {
  void i18n.changeLanguage(code);
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* 無 storage */
  }
}

// 家戶在畫面上的名稱（SPEC #terms-home-ui-name）：字串裡寫 $t(leaf.cur) / $t(leaf.cur_lc) / $t(leaf.cur_pl) / $t(leaf.cur_lc_pl)
// （kmgr 命名空間寫 $t(translation:leaf.cur)），值由這裡依目前組織寫進每個語系的 leaf.*——巢狀翻譯跟著外層的語系走，
// getFixedT('ja') 這類指定語系的呼叫也對。JSON 裡的初始值 = team 預設（地點），範圍載入後 LeafTermSync 呼叫 setLeafContext。
/** 設目前組織的種類與 leaf_label；值變了回 true（呼叫端讓畫面重畫）。 */
export function setLeafContext(kind: 'personal' | 'team', label?: string | null): boolean {
  let changed = false;
  for (const { code } of LANGS) {
    const v = leafVars(i18n.getFixedT(code), kind, label);
    const want: Record<string, string> = { cur: v.leaf, cur_lc: v.leaf_lc, cur_pl: v.leaf_pl, cur_lc_pl: v.leaf_lc_pl };
    for (const [k, val] of Object.entries(want)) {
      if (i18n.getResource(code, 'translation', 'leaf.' + k) === val) continue;
      i18n.addResource(code, 'translation', 'leaf.' + k, val, { silent: true });
      changed = true;
    }
  }
  return changed;
}

export default i18n;
